import React, { useEffect, useState } from "react";
import styled from "styled-components";
import LineBtn from "../../../Button/LineBtn";
import DotStatus from "../../../DefaultStyles/DotStatus";
import Flex from "../../../DefaultStyles/Flex";
import LineBox from "../../../DefaultStyles/LineBox";
import SpaceBetween from "../../../DefaultStyles/SpaceBetween";
import CommentModal from "./CommentModal/CommentModal";
import BlackListModal from "./BlackListModal/BlackListModal";
import { AxiosRequestConfig } from "axios";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";
import { authType } from "../../../../pages/Login";
import { useNavigate } from "react-router-dom";
import { IBlackListInfo } from "../../../../pages/Users/BlackList";
import LinkidMoneySettingModal from "./LinkidMoneySettingModal/LinkidMoneySettingModal";
import { useApi } from "../../../../hooks/useApi";
import { userCommentListState } from "./UserDetailComment";
import { newUserLogFilterState } from "../../../../pages/NewUserLog/NewUserLog";

interface UserDetailHeaderProps {
  userId?: string;
  data: {
    uid?: string;
    name?: string;
    nickname?: string;
    blackinfo?: IBlackListInfo[];
    usemoney?: number;
  };
  fetchData: (params: AxiosRequestConfig) => Promise<void>;
  loading: boolean;
}

export const showIpLogState = atom({
  key: "showIpLogState",
  default: {
    state: false,
    uid: 0,
  },
});

export default function UserDetailHeader({
  userId,
  data,
  fetchData,
  loading,
}: UserDetailHeaderProps) {
  const auth = useRecoilValue(authType);
  const navigate = useNavigate();

  const [moneyPriorityModal, setMoneyPriorityModal] = useState(false);
  const [commentWriteModal, setCommentWriteModal] = useState(false);
  const [blackListModal, setBlackListModal] = useState(false);
  const [blackListViewModal, setBlackListViewModal] = useState(false);
  const [blacklistData, setBlacklistData] = useState<
    IBlackListInfo | undefined
  >();
  const setNewUserlogFilter = useSetRecoilState(newUserLogFilterState);
  const [fetchComment, setFetchComment] = useState(false);
  const setCommentList = useSetRecoilState(userCommentListState);

  const [userData, setUserData] = useState({
    uid: "",
    name: "",
    nickname: "",
  });

  const saveUserData = () => {
    setUserData({
      name: data?.name ?? "",
      nickname: data?.nickname ?? "",
      uid: userId ?? "",
    });
  };

  const isBlackList = (data?.blackinfo?.length ?? 0) > 0 ? true : false;

  const { response } = useApi({
    method: "get",
    url: fetchComment ? `comment?page=0&size=5&uid=${userId}` : undefined,
  });

  useEffect(() => {
    if (response?.status === 200 && response?.data?.items?.length > 0) {
      setCommentList(response?.data?.items);
      setFetchComment(false);
    }
  }, [response]);

  useEffect(() => {}, [data, fetchData]);

  return (
    <>
      <LineBox notScroll={true} boxHeight="80px" center={true}>
        <SpaceBetween>
          <>
            <Flex gapSize={12}>
              <>
                <Uid>{userId}</Uid>

                <Name>
                  {data?.name}_{data?.nickname}
                </Name>

                {isBlackList && <DotStatus text="블랙리스트" />}
              </>
            </Flex>

            <Flex gapSize={10}>
              <>
                <LineBtn
                  btnText="코멘트 입력"
                  icon="edit"
                  btnClickEvent={() => {
                    saveUserData();
                    setCommentWriteModal(true);
                  }}
                  disabled={loading}
                />

                <LineBtn
                  btnText={isBlackList ? "블랙리스트 수정" : "블랙리스트 추가"}
                  icon="sentiment_very_dissatisfied"
                  btnClickEvent={() => {
                    saveUserData();

                    if (isBlackList) {
                      setBlacklistData(
                        data?.blackinfo ? data?.blackinfo[0] : undefined
                      );
                      setBlackListViewModal(true);
                    } else {
                      setBlackListModal(true);
                    }
                  }}
                  disabled={loading}
                />

                <LineBtn
                  btnText="우선 결제 옵션"
                  icon="credit_score"
                  btnClickEvent={() => {
                    saveUserData();
                    setMoneyPriorityModal(true);
                  }}
                  disabled={loading || !auth?.bill}
                />

                <LineBtn
                  btnText="NEW 유저 로그 조회"
                  icon="launch"
                  disabled={loading}
                  btnClickEvent={() => {
                    setNewUserlogFilter((prev) => ({
                      ...prev,
                      searchType: "uid",
                      searchValue: userId + "",
                    }));
                    navigate("/new-userlog");
                  }}
                />

                {/* 코멘트 입력 모달 */}
                {commentWriteModal && (
                  <CommentModal
                    closeEvent={() => setCommentWriteModal(false)}
                    type="input"
                    commentData={userData}
                    confirmEvent={() => setFetchComment(true)}
                  />
                )}

                {/* 블랙리스트 추가 모달 */}
                {blackListModal && (
                  <BlackListModal
                    closeEvent={() => setBlackListModal(false)}
                    confirmEvent={() => {
                      fetchData({
                        method: "get",
                        url: `user/${userId}`,
                      });
                    }}
                    type="input"
                    blacklistData={userData}
                  />
                )}

                {/* 블랙리스트 보기 모달 (버튼 텍스트는 블랙리스트 수정) */}
                {blackListViewModal && (
                  <BlackListModal
                    closeEvent={() => setBlackListViewModal(false)}
                    confirmEvent={() => {
                      fetchData({
                        method: "get",
                        url: `user/${userId}`,
                      });
                    }}
                    type="view"
                    blacklistData={blacklistData}
                  />
                )}

                {/* 링키드 머니 우선 결제 옵션 모달 */}
                {moneyPriorityModal && (
                  <LinkidMoneySettingModal
                    closeEvent={() => setMoneyPriorityModal(false)}
                    confirmEvent={() => {
                      fetchData({
                        method: "get",
                        url: `user/${data?.uid}`,
                      });
                    }}
                    prevValue={data?.usemoney ?? 100}
                    userData={userData}
                  />
                )}
              </>
            </Flex>
          </>
        </SpaceBetween>
      </LineBox>
    </>
  );
}

const Uid = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 39px;
  text-align: left;
  color: #2f2f38;
  word-break: keep-all;
`;

const Name = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  color: #2f2f38;
  word-break: keep-all;
`;
